import { cn } from '@sohanemon/utils';
import { MARKDOWN_EXTENSIONS } from '@sohanscript/markdown';
import { createHeadingIdFactory } from '@sohanscript/markdown/heading-ids';
import { Markdown } from '@tanstack/markdown/react';
import * as React from 'react';
import {
	type MarkdownComponentRegistry,
	type RenderImage,
	useMarkdownComponents,
} from './components';

export type {
	MarkdownComponentRegistry,
	RenderImage,
	RenderImageProps,
} from './components';

const PROSE_CLASS =
	'prose max-w-none font-souvenir [&_:not(pre)>code]:rounded [&_:not(pre)>code]:bg-primary/10 [&_:not(pre)>code]:px-1 [&_:not(pre)>code]:py-0.5 [&_:not(pre)>code]:text-primary [&_:not(pre)>code]:before:content-none [&_:not(pre)>code]:after:content-none [&_blockquote]:border-primary/40 [&_blockquote]:border-s [&_blockquote]:ps-4';

interface MarkdownContentProps {
	content: string;
	/**
	 * Enables raw HTML rendering and resolves relative image URLs against this
	 * base. Only set for trusted content (e.g. your own GitHub README). Never set
	 * for user-generated content, it would expose the page to XSS.
	 */
	imgBase?: string;
	/**
	 * Renders image elements instead of the default plain `<img>`; plug the
	 * host app's image component here.
	 */
	renderImage?: RenderImage;
	components?: MarkdownComponentRegistry;
	as?: React.ElementType;
	className?: string;
}

export function MarkdownContent({
	content,
	imgBase,
	renderImage,
	components: directiveComponents,
	as: Tag = 'article',
	className,
}: MarkdownContentProps) {
	// Stable component map, otherwise every render rebuilds the element tree.
	const components = useMarkdownComponents({
		imgBase,
		renderImage,
		components: directiveComponents,
	});
	// NOTE: the factory dedupes ids, records heading text and resets itself when
	// the parser rewinds its line cursor, so re-parsing the same content yields
	// the same ids.
	const headingIds = createHeadingIdFactory();

	// NOTE: `React.createElement` for the dynamic tag; JSX cannot type children
	// against a bare `React.ElementType`.
	return React.createElement(
		Tag,
		{
			className: cn(
				PROSE_CLASS,
				'w-full min-w-0 flex-1 text-foreground',
				className,
			),
		},
		<Markdown
			components={components}
			extensions={MARKDOWN_EXTENSIONS}
			headingIds={headingIds}
			// NOTE: the parser eats a leading `---` block as frontmatter and never
			// renders it; the old pipeline rendered it, so keep it off.
			frontmatter={false}
			allowHtml={imgBase ? true : undefined}
			imgBase={imgBase}
		>
			{content}
		</Markdown>,
	);
}
